<template>
  <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
    <v-card-title class="catchment-map-header white--text text-subtitle-1 d-flex justify-space-between align-center">
      <div class="catchment-map-title d-flex align-center">
        <v-icon left color="primary">mdi-map-marker-radius</v-icon>
        <span>Einzugsgebiet (Heatmap)</span>
      </div>
      <div class="catchment-map-actions">
        <v-chip outlined class="catchment-map-total">
          <v-icon left small>mdi-account-group</v-icon>
          {{ totalCustomersOnMap }} Kunden dargestellt
        </v-chip>
        <statistic-export-menu
          title="Einzugsgebiet nach PLZ"
          file-key="tac-einzugsgebiet-plz"
          :date="exportDate"
          :series="[{ name: 'Kunden', data: mapData }]"
          :summary="[
            { label: 'Dargestellte Kunden', value: totalCustomersOnMap },
            { label: 'PLZ-Gebiete', value: mapDataInternal ? mapDataInternal.length : 0 },
          ]"
          accent="#9c27b0"
          :disabled="loading" />
      </div>
    </v-card-title>

    <div style="height: 500px; position: relative; width: 100%">
      <div v-if="loading" class="d-flex justify-center align-center fill-height" style="background: #2b2b2b">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <span class="ml-3 grey--text">Lade Geodaten...</span>
      </div>

      <l-map v-else ref="map" :zoom="zoom" :center="center" :options="mapOptions" style="height: 100%; width: 100%; z-index: 1">
        <l-tile-layer :url="tileUrl" :attribution="attribution" />

        <l-marker
          v-for="studio in studios"
          :key="'studio-' + studio._id"
          :lat-lng="[studio.location.lat, studio.location.long]"
          :icon="getStudioIcon(studio)">
          <l-tooltip>{{ studio.name }}</l-tooltip>
        </l-marker>

        <l-circle-marker
          v-for="(zone, index) in mapDataInternal"
          :key="'plz-' + index"
          :lat-lng="[zone.lat, zone.lon]"
          :radius="calcRadius(zone.count)"
          :color="calcColor(zone.count)"
          :fillColor="calcColor(zone.count)"
          :fillOpacity="0.4"
          :weight="1"
          :stroke="true">
          <l-tooltip>
            <div class="text-center">
              <strong>PLZ {{ zone.plz }}</strong
              ><br />
              {{ zone.count }} Kunden
            </div>
          </l-tooltip>
        </l-circle-marker>

        <div class="map-legend">
          <div class="d-flex align-center mb-1"><span class="legend-dot" style="background: #ffeb3b"></span> Wenig</div>
          <div class="d-flex align-center mb-1"><span class="legend-dot" style="background: #ff9800"></span> Mittel</div>
          <div class="d-flex align-center"><span class="legend-dot" style="background: #f44336"></span> Viele</div>
        </div>
      </l-map>

      <v-btn absolute bottom right small fab color="white" class="black--text mb-6 mr-2" style="z-index: 400" @click="fitBounds">
        <v-icon>mdi-image-filter-center-focus</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import L from "leaflet"
import { LMap, LTileLayer, LMarker, LCircleMarker, LTooltip } from "vue2-leaflet"
import "leaflet/dist/leaflet.css"
import geoService from "@/services/geoService" // Pfad anpassen
import StatisticExportMenu from "./StatisticExportMenu.vue"

// Fix für Leaflet Icons in Webpack/Vue
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

export default {
  name: "CatchmentMap",
  components: { LMap, LTileLayer, LMarker, LCircleMarker, LTooltip, StatisticExportMenu },
  props: {
    mapData: {
      type: Array,
      default: () => [],
    },
    studios: {
      type: Array,
      default: () => [],
    },
    exportDate: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: true,
      zoom: 9,
      center: [47.4, 8.6], // Default Schweiz/Zürich Umgebung
      // Dunkle Karte sieht "cooler" und professioneller aus für Dashboards
      tileUrl: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',

      mapData: [],
      maxCount: 1, // Für Scaling der Farben
      mapDataInternal: null,
      mapOptions: {
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
      },
    }
  },
  computed: {
    totalCustomersOnMap() {
      // Sicherheitscheck, falls Daten noch nicht verarbeitet sind
      if (!this.mapDataInternal) return 0

      // Wir summieren 'count' aus den verarbeiteten Daten
      return this.mapDataInternal.reduce((acc, curr) => acc + (curr.count || 0), 0)
    },
  },
  watch: {
    mapData: {
      // <--- Auf die neue Prop hören
      handler() {
        this.processData()
      },
      deep: true,
    },
    studios() {
      setTimeout(() => this.fitBounds(), 500)
    },
  },
  async mounted() {
    this.loading = true
    await geoService.init()
    this.processData()
    this.loading = false
  },
  methods: {
    processData() {
      // Wir nutzen jetzt this.mapData direkt (kein series[0].data mehr nötig)
      const rawData = this.mapData || []

      let processed = []
      let max = 0

      rawData.forEach((item) => {
        const plz = item.x
        const count = item.y

        if (count > max) max = count

        const coords = geoService.getCoordinates(plz)
        if (coords) {
          processed.push({
            plz: plz,
            count: count,
            lat: coords.lat,
            lon: coords.lon,
          })
        }
      })

      this.maxCount = max > 0 ? max : 1
      this.mapDataInternal = processed // Nutze eine interne Variable für die gerenderte Liste

      this.$nextTick(() => {
        this.fitBounds()
      })
    },

    calcRadius(count) {
      // 1. Basis-Grösse basierend auf Kundenanzahl (Logarithmisch)
      let baseRadius = 0
      if (count <= 1) baseRadius = 3
      else baseRadius = 3 + Math.log(count) * 2.5

      // 2. Zoom-Faktor berechnen
      // Wir nehmen Zoom 8 als Basis. Für jede Stufe tiefer vergrössern wir den Radius.
      // Faktor 1.4 sorgt dafür, dass die Punkte beim Reinzoomen schön mitwachsen.
      let zoomFactor = 1
      if (this.currentZoom > 8) {
        zoomFactor = Math.pow(1.3, this.currentZoom - 8)
      }

      return baseRadius * zoomFactor
    },

    calcColor(count) {
      // Farbe bleibt wie gehabt, aber wir ändern die Deckkraft im Template!
      const ratio = count / this.maxCount
      if (ratio < 0.1) return "#FFEB3B" // Gelb für wenige
      if (ratio < 0.4) return "#FF9800" // Orange
      return "#F44336" // Rot
    },

    getStudioIcon(studio) {
      // Ein spezielles Icon für die Studios, damit sie sich abheben
      // Wir basteln ein HTML Icon
      return L.divIcon({
        className: "custom-studio-icon",
        html: `<div style="background-color: #00E676; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px #00E676;"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      })
    },

    fitBounds() {
      if (!this.$refs.map) return
      const map = this.$refs.map.mapObject

      // Wir sammeln alle Punkte (Studios + PLZ)
      let bounds = []

      this.studios.forEach((s) => {
        if (s.location && s.location.lat) bounds.push([s.location.lat, s.location.long])
      })

      this.mapData.forEach((m) => {
        bounds.push([m.lat, m.lon])
      })

      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50] })
      }
    },
  },
}
</script>

<style scoped>
.catchment-map-title {
  min-width: 0;
}

.catchment-map-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.375rem;
}

.catchment-map-total {
  min-height: 2rem;
  margin: 0;
  font-size: 0.75rem;
}

.map-legend {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  border-radius: 8px;
  color: white;
  z-index: 400;
  backdrop-filter: blur(5px);
  font-size: 12px;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
}

::v-deep .leaflet-control-attribution {
  display: none !important;
}

@media (max-width: 37.5rem) {
  .catchment-map-header {
    align-items: flex-start !important;
    gap: 0.75rem;
  }

  .catchment-map-actions {
    align-items: flex-end;
    flex-direction: column-reverse;
  }

  .catchment-map-total {
    white-space: normal;
  }
}
</style>
